import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

// Mirrors backend/turnus_ai/mc_constants.py MASTER_DATA_*_STATUSES.
export const SUPPLIER_STATUS_VALUES = [
  "waiting",
  "needs_review",
  "missing_data",
  "complete",
] as const

export const ARTICLE_STATUS_VALUES = [
  "waiting",
  "needs_review",
  "missing_data",
  "compliant",
  "compliant_with_exemption",
  "non_compliant",
] as const

export const PRODUCT_STATUS_VALUES = ARTICLE_STATUS_VALUES

type StatusValue =
  | (typeof SUPPLIER_STATUS_VALUES)[number]
  | (typeof ARTICLE_STATUS_VALUES)[number]

export const STATUS_LABEL_KEYS = {
  waiting: "MC_MD_ST_WAITING",
  needs_review: "MC_MD_ST_NEEDS_REVIEW",
  missing_data: "MC_MD_ST_MISSING_DATA",
  complete: "MC_MD_ST_COMPLETE",
  compliant: "MC_MD_ST_COMPLIANT",
  compliant_with_exemption: "MC_MD_ST_COMPLIANT_WITH_EXEMPTION",
  non_compliant: "MC_MD_ST_NON_COMPLIANT",
} satisfies Record<StatusValue, TranslationKeys>

type Translate = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

/**
 * Translated label for a supplier/article/product status value. Falls back to
 * the raw status for anything outside STATUS_LABEL_KEYS (e.g. legacy/import
 * data that predates the canonical vocabulary) rather than rendering nothing.
 */
export function productStatusLabel(
  status: string,
  translate: Translate,
): string {
  const key = (STATUS_LABEL_KEYS as Record<string, TranslationKeys>)[status]
  return key ? translate(key) : status
}

/**
 * Adapts a single-valued query param (e.g. `supplier_id`) to the multi-select
 * FacetedFilter widget: picks whichever value differs from the current one
 * (the one the user just clicked), so the control behaves as single-select.
 */
export function singleSelectFacet(
  value: string | null,
  onChange: (value: string | null) => void,
) {
  return {
    selected: value ? [value] : [],
    onSelectionChange: (values: string[]) => {
      onChange(values.find((v) => v !== value) ?? values[0] ?? null)
    },
  }
}
